<template>
  <div class="year-summary-container">
    <div class="summary-content">
      <!-- ヘッダー部分 -->
      <div class="header-section">
        <div class="header-wrapper">
          <div class="title-line-1">
            <div class="title-box">
              <div class="title-content">
                <div class="title-text">{{ $t('historiesLookBackYear1', { year }) }}</div>
              </div>
            </div>
          </div>
          <div class="title-line-2">
            <div class="title-box">
              <div class="title-content">
                <div class="title-text">{{ $t('historiesLookBackYear2') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 統計カード部分 -->
      <div class="stats-section">
        <div class="stats-wrapper">
          <div class="stats-cards">
            <!-- 総視聴時間カード -->
            <div class="stat-card watch-time-card">
              <div class="card-icon">
                <img 
                  src="/img/campaign/reality_histories_20m/img_play.svg"
                  alt="再生アイコン" 
                  class="icon-img"
                />
              </div>
              <div class="card-label">{{ $t('totalViewingTime') }}</div>
              <div class="card-value">{{ watchTime }}</div>
            </div>

            <!-- 総配信時間カード -->
            <div class="stat-card stream-time-card">
              <div class="card-icon">
                <img 
                  src="/img/campaign/reality_histories_20m/img_Video.svg"
                  alt="ビデオアイコン" 
                  class="icon-img"
                />
              </div>
              <div class="card-label">{{ $t('totalBroadcastingTime') }}</div>
              <div class="card-value">{{ streamTime }}</div>
            </div>
          </div>

          <!-- 盛り上がった配信カード -->
          <div v-if="hasBroadcastData" class="featured-stream-card">
            <div class="featured-content">
              <div class="live-badge">
                <img 
                  src="/img/campaign/reality_histories_20m/img_live_badge.svg"
                  alt="ライブバッジ" 
                  class="live-badge-img"
                />
              </div>
              <div class="stream-label">{{ $t('historiesMostViewedBroadcast') }}</div>
              <div class="stream-info">
                <div class="stream-title">{{ featuredStream.title }}</div>
                <div class="stream-date">{{ featuredStream.date }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import { HistoryAlbumKeyedData } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumKeyedData'
import { HistoryAlbumUser } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumUser'
import { formatDate } from '@/pages/campaign/reality_histories_20m/util'

interface FeaturedStream {
  title: string
  date: string
}

@Component({})
export default class YearSummaryCard extends Vue {
  @Prop({ type: Number, required: true })
  year!: number

  // 年別データをpropsで受け取る
  @Prop({ type: Object, default: null })
  yearData!: HistoryAlbumKeyedData | null

  // ユーザー情報をpropsで受け取る
  @Prop({ type: Object, default: null })
  user!: HistoryAlbumUser | null

  // 視聴時間を取得（実際のデータから）
  get watchTime(): string {
    if (this.yearData && this.yearData.myRecord && this.yearData.myRecord.totalBroadcastViewHour !== undefined) {
      const hours = this.yearData.myRecord.totalBroadcastViewHour
      return hours === 0 ? '--' : `${hours.toLocaleString()}h`
    }
    return '--'
  }

  // 配信時間を取得（実際のデータから）
  get streamTime(): string {
    if (this.yearData && this.yearData.myRecord && this.yearData.myRecord.totalBroadcastHour !== undefined) {
      const hours = this.yearData.myRecord.totalBroadcastHour
      return hours === 0 ? '--' : `${hours.toLocaleString()}h`
    }
    return '--'
  }

  // 活動があったかどうかを判定
  get hasActivity(): boolean {
    if (!this.yearData || !this.yearData.myRecord) {
      return false
    }
    const viewHours = this.yearData.myRecord.totalBroadcastViewHour || 0
    const broadcastHours = this.yearData.myRecord.totalBroadcastHour || 0
    return viewHours > 0 || broadcastHours > 0
  }

  // 配信データがあるかどうかを判定
  get hasBroadcastData(): boolean {
    if (!this.yearData || !this.yearData.myRecord) {
      return false
    }
    const broadcastHours = this.yearData.myRecord.totalBroadcastHour || 0
    return broadcastHours > 0 && this.yearData.broadcastRecords && this.yearData.broadcastRecords.length > 0
  }

  // 盛り上がった配信情報を取得
  get featuredStream(): FeaturedStream {
    if (this.yearData && this.yearData.broadcastRecords && this.yearData.broadcastRecords.length > 0) {
      // データはすでにソートされているので最初の要素を取得
      const mostPopularStream = this.yearData.broadcastRecords[0]
      
      return {
        title: mostPopularStream.liveName,
        date: mostPopularStream.liveDate ? formatDate(mostPopularStream.liveDate) : ''
      }
    }
    
    // データがない場合のデフォルト表示
    return {
      title: String(this.$t('historiesNoFeaturedBroadcast')),
      date: ''
    }
  }

}
</script>

<style scoped>
.year-summary-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.summary-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  position: relative;
  width: 100%;
  height: 100%;
}

/* ヘッダー部分 */
.header-section {
  position: relative;
  width: 100%;
  flex-shrink: 0;
}

.header-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
}

.title-line-1,
.title-line-2 {
  position: relative;
  flex-shrink: 0;
}

.title-box {
  background: #171636;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 39px;
}

.title-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  position: relative;
}

.title-text {
  font-weight: 600;
  font-style: normal;
  font-size: 26px;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
  line-height: normal;
  position: relative;
  flex-shrink: 0;
}

/* 統計部分 */
.stats-section {
  position: relative;
  width: 100%;
  flex-shrink: 0;
}

.stats-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
}

.stats-cards {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  position: relative;
  width: 100%;
}

/* 統計カード */
.stat-card {
  flex: 1;
  height: 88px;
  min-height: 1px;
  min-width: 1px;
  position: relative;
  border-radius: 8px;
  flex-shrink: 0;
}

/* 視聴時間カード用グラデーション */
.watch-time-card {
  background: linear-gradient(117deg, #4E95FF 0%, #6DF2FF 100%);
}

/* 配信時間カード用グラデーション */
.stream-time-card {
  background: linear-gradient(117deg, #5AD7E2 0%, #ABFD9B 100%);
}

.card-icon {
  position: absolute;
  right: 0.5px;
  width: 65px;
  height: 65px;
  top: 0;
}

.icon-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.card-label {
  position: absolute;
  left: 8px;
  top: 8px;
  font-weight: 300;
  font-style: normal;
  font-size: 11px;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
  line-height: normal;
}

.card-value {
  position: absolute;
  left: 8px;
  top: 57px;
  transform: translateY(-50%);
  width: 151px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-weight: 900;
  font-style: normal;
  font-size: 32px;
  color: #ffffff;
  text-align: left;
  line-height: 24px;
}

/* 盛り上がった配信カード */
.featured-stream-card {
  background: linear-gradient(117deg, #DC7EFF 0%, #8CAAFD 100%), rgba(197, 209, 255, 0.50);
  position: relative;
  border-radius: 8px;
  width: 100%;
  flex-shrink: 0;
}

.featured-content {
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column-reverse;
  gap: 12px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 12px 16px;
}

.stream-label {
  font-weight: 300;
  line-height: normal;
  font-style: normal;
  order: 3;
  position: relative;
  flex-shrink: 0;
  color: #ffffff;
  font-size: 11px;
  text-align: left;
  width: 210.929px;
}

.stream-info {
  order: 2;
  position: relative;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  line-height: normal;
  font-style: normal;
  padding: 0;
  color: #ffffff;
  text-align: left;
}

.stream-title {
  font-weight: 600;
  min-width: 100%;
  position: relative;
  flex-shrink: 0;
  font-size: 20px;
  line-height: normal;
}

.stream-date {
  font-weight: 300;
  position: relative;
  flex-shrink: 0;
  font-size: 11px;
  width: 210.929px;
  line-height: normal;
}

.live-badge {
  position: absolute;
  height: 70px;
  order: 1;
  overflow: hidden;
  right: 0;
  top: 0;
  width: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.live-badge-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
</style>
